import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { useSession } from "../session";

const titles: Record<string, { title: string; sub: string }> = {
  "/": { title: "Leads", sub: "Captured by FMC and delivered to you" },
  "/delivery": { title: "Delivery", sub: "How leads leave the platform" },
  "/team": { title: "Team", sub: "Users and access levels" },
  "/settings": { title: "Settings", sub: "Brand & notifications" },
};

export function Layout() {
  const { pathname, search, hash } = useLocation();
  const session = useSession();
  const meta = titles[pathname] ?? { title: "Leads Manager", sub: "" };

  if (session.loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-secondary">Loading…</div>
    );
  if (!session.authenticated) {
    if (session.expiryTarget === "admin") {
      return <Navigate to="/admin/login?reason=session-expired" replace />;
    }
    const returnTo = `${pathname}${search}${hash}`;
    const params = new URLSearchParams({ returnTo });
    if (session.expiryTarget === "client") params.set("reason", "session-expired");
    return <Navigate to={`/login?${params.toString()}`} replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-surface-light">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <ImpersonationBanner />
        <header className="shrink-0 h-[64px] bg-surface-white border-b border-default flex items-center justify-between px-[30px]">
          <div className="flex items-baseline gap-2.5 min-w-0">
            <h1 className="text-title-lg font-bold text-primary truncate">{meta.title}</h1>
            {meta.sub && <span className="text-body text-tertiary truncate">{meta.sub}</span>}
          </div>
          <div className="flex items-center gap-5">
            <div className="flex items-center">
              <span className="text-small font-semibold text-primary">{session.brand}</span>
            </div>
            <div className="h-6 w-px bg-default" />
            <button className="flex items-center gap-2.5">
              <span
                className={`w-8 h-8 rounded-full flex items-center justify-center text-caption font-semibold ${
                  session.impersonating
                    ? "bg-primary text-white"
                    : "bg-surface-info text-brand-dark-teal"
                }`}
              >
                {session.userName
                  .split(" ")
                  .map((part) => part[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span className="text-small font-medium text-primary">
                {session.userName}
                {session.impersonating ? " (FMC)" : ""}
              </span>
              <i className="fa-solid fa-chevron-down text-[11px] text-tertiary" />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto scroll-thin">
          <div className="px-[30px] py-[30px] max-w-[1240px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
